import {
  GetOperation,
  GetListOperation,
  PostOperation,
} from '@/api/rest/base/operations';
import BaseApi from './base/BaseApi';

/**
 * Спецификация проекта и её подресурсы (ТЗ 4.1): позиции приходят вместе со спецификацией,
 * рядом — свод, запросы КП, история, чаты, ведомость, ссылка заказчика, активные задания AI.
 * Адреса — по отчётам бэкенда этапов 1–6. Сгенерировано Claude.
 */
export default class SpecificationApi extends BaseApi {
  static entityName = 'specifications';

  /** Спецификация проекта; при первом обращении создаётся сервером (ТЗ 4.1). */
  fetchByProject(projectId, withPreloader = false) {
    const operation = new GetOperation(`/api/projects/${projectId}/specification`);
    return this.execute(operation, withPreloader);
  }

  /** Пакетное действие по позициям; ответ — спецификация со сводом (ТЗ 4.1). */
  batch(id, params, withPreloader = false) {
    const operation = new PostOperation(`${this.getIri(id)}/items/batch`, params);
    return this.execute(operation, withPreloader);
  }

  /** Поставщики, подходящие под выделенные позиции (ТЗ 4.1). */
  fetchRequestSuppliers(id, params = {}, withPreloader = false) {
    const operation = new GetListOperation(`${this.getIri(id)}/request_suppliers`, params);
    return this.execute(operation, withPreloader);
  }

  /** Создать запросы КП: { items, suppliers, showBudget } (ТЗ 4.1). */
  createRequests(id, params, withPreloader = false) {
    const operation = new PostOperation(`${this.getIri(id)}/requests`, params);
    return this.execute(operation, withPreloader);
  }

  /** Список запросов КП спецификации для панели «Запросы» (ТЗ 4.1, 6.2). */
  fetchRequests(id, withPreloader = false) {
    const operation = new GetListOperation(`${this.getIri(id)}/requests`);
    return this.execute(operation, withPreloader);
  }

  /** История спецификации: параметры item и kind (ТЗ 4.1). */
  fetchHistory(id, params = {}, withPreloader = false) {
    const operation = new GetListOperation(`${this.getIri(id)}/history`, params);
    return this.execute(operation, withPreloader);
  }

  /** Чаты спецификации (ТЗ 4.1, 6.10). */
  fetchChats(id, withPreloader = false) {
    const operation = new GetListOperation(`${this.getIri(id)}/chats`);
    return this.execute(operation, withPreloader);
  }

  /** Ведомость поставки (ТЗ 4.1, 6.3). */
  fetchSupply(id, hideAccepted = false, withPreloader = false) {
    const params = { hideAccepted: hideAccepted ? 1 : 0 };
    const operation = new GetOperation(`${this.getIri(id)}/supply`, params);
    return this.execute(operation, withPreloader);
  }

  /** Блок «Комплектация» проекта (ТЗ 4.1, 7.3). */
  fetchProcurement(projectId, withPreloader = false) {
    const operation = new GetOperation(`/api/projects/${projectId}/procurement`);
    return this.execute(operation, withPreloader);
  }

  /** Готовая ссылка заказчика (ТЗ 4.1). */
  fetchCustomerLink(id, withPreloader = false) {
    const operation = new GetOperation(`${this.getIri(id)}/customer_link`);
    return this.execute(operation, withPreloader);
  }

  /** Перевыпуск ссылки заказчика (ТЗ 4.1). */
  reissueCustomerLink(id, withPreloader = false) {
    const operation = new PostOperation(`${this.getIri(id)}/customer_link/reissue`, {});
    return this.execute(operation, withPreloader);
  }

  /** Активные задания AI спецификации (ТЗ 4.1, 5.1). */
  fetchActiveJobs(id, withPreloader = false) {
    const operation = new GetListOperation(`${this.getIri(id)}/ai_jobs/active`);
    return this.execute(operation, withPreloader);
  }

  /**
   * Адрес выгрузки таблицы (ТЗ 4.1, 7.6). Ссылкой, а не запросом: файл отдаётся с заголовком
   * вложения, и браузер сохраняет его сам.
   */
  exportUrl(id, { format, view, group, items = [] }) {
    const query = [
      `format=${encodeURIComponent(format)}`,
      `view=${encodeURIComponent(view)}`,
      `group=${encodeURIComponent(group)}`,
      ...items.map((itemId) => `items[]=${encodeURIComponent(itemId)}`),
    ];
    return `${this.getIri(id)}/export?${query.join('&')}`;
  }

  /** Адрес выгрузки ведомости (ТЗ 4.1, 7.6). */
  supplyExportUrl(id, { format, supplier = null, hideAccepted = false }) {
    const query = [
      `format=${encodeURIComponent(format)}`,
      `hideAccepted=${hideAccepted ? 1 : 0}`,
    ];
    if (supplier) query.push(`supplier=${encodeURIComponent(supplier)}`);
    return `${this.getIri(id)}/supply/export?${query.join('&')}`;
  }
}
